import React, { useState, ChangeEvent } from 'react';
import { useParams } from 'react-router-dom';
import * as Components from './components';
import { BackButton } from 'components/buttons';
import EditEnvironmentModal from './components/EditEnvironmentModal';
import Header, { ButtonConfig } from 'components/header/Header';
import SimpleLoader from 'components/loader/SimpleLoader';
import TabsPanel from 'components/tabs/TabsPanel';
import StatusWithTooltip from 'components/StatusWithTooltip/StatusWithTooltip';
import { SageMakerView } from '../Sagemaker';
import { useModal } from 'hooks/useModal';
import EnvironmentStackDetails from 'views/Stack/EnvironmentStackDetails';
import useEnvironment from './hooks/useEnvironment';
import DeleteModal from 'components/modal/DeleteModal';
import useArchiveEnvironment from './hooks/useArchiveEnvironment';
import useEnvironmentDetails, {
    TableAccount,
} from './hooks/useEnvironmentDetails';
import EditorDetails from 'components/details/EditorDetails';
import { NButton, NTable } from '@innersource-nn/novoui-react';
import { useNavigate } from 'react-router-dom';

const EnvironmentView = () => {
    const navigate = useNavigate();
    const params = useParams<{ uri: string }>();
    const environmentUri = params.uri;
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [counter, setCounter] = useState(1);
    const [selectedTab, setSelectedTab] = useState(0);
    const { environment, userIsAdmin, loading, error } = useEnvironment();
    const { handleArchiveEnvironment, deleteLoading } = useArchiveEnvironment();
    const { openAWSResourcesModal } = useModal();
    const { items, detailsError, detailsLoading, fetchData, tableAccounts } =
        useEnvironmentDetails();

    const buttonTooltip = !userIsAdmin
        ? 'To access this functionality you need to be a Creator, Admin or Owner of this Environment'
        : '';

    const buttonConfig: ButtonConfig[] = [
        {
            label: 'Edit',
            onClick: () => setEditModalOpen(true),
            icon: 'edit',
            disabled: !userIsAdmin,
            tooltip: buttonTooltip,
        },
        {
            label: 'Console',
            onClick: () =>
                openAWSResourcesModal({
                    environmentUri: params.uri ?? '',
                    title: 'Console',
                }),
            icon: 'external',
            disabled: environmentUri === undefined,
            tooltip: buttonTooltip,
        },
        {
            label: 'Archive',
            onClick: () => setDeleteModalOpen(true),
            red: true,
            icon: 'trash',
            disabled: !userIsAdmin,
            tooltip: buttonTooltip,
        },
    ];

    const adjustItems = (rows: TableAccount[]) => {
        return rows.map((row) => {
            return {
                ...row,
                action: (
                    <div className="flex gap-8">
                        <NButton
                            size="tiny"
                            variant="outline"
                            square
                            onClick={() => {
                                setSelectedTab(1);
                                navigate(
                                    `/environment/${environmentUri}/?tab=1&accounts=${row.name}`,
                                    { replace: true },
                                );
                            }}
                        >
                            View Datasets
                        </NButton>
                    </div>
                ),
            };
        });
    };

    const tableHeaders = [
        {
            header: 'Name',
            key: 'name',
        },
        { header: 'AWS Account', key: 'awsAccount' },
        { header: 'Action', key: 'action' },
    ];

    return (
        <div>
            <DeleteModal
                title="Delete environment"
                type="environment"
                onClick={handleArchiveEnvironment}
                loading={deleteLoading}
                visible={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
            />
            <EditEnvironmentModal
                visible={editModalOpen}
                onClose={() => {
                    fetchData();
                    setEditModalOpen(false);
                }}
            />
            {loading ? (
                <SimpleLoader />
            ) : (
                !error && (
                    <div>
                        <BackButton counter={counter} />
                        <Header
                            title={environment.label}
                            buttonConfigs={buttonConfig}
                        />
                        <div className="flex gap-8 items-center">
                            <div className="text-true-blue text-sm">
                                Created: {environment.created}
                            </div>
                            <StatusWithTooltip
                                content={environment.stack.status}
                            />
                        </div>
                        <TabsPanel
                            counter={counter}
                            setCounter={setCounter}
                            tabs={[
                                {
                                    label: 'overview',
                                    component: (
                                        <EditorDetails
                                            loading={detailsLoading}
                                            error={detailsError}
                                            items={items}
                                            type="environment"
                                        />
                                    ),
                                },
                                {
                                    label: 'datasets',
                                    component: <Components.DatasetList />,
                                },
                                {
                                    label: 'stack ',
                                    component: (
                                        <EnvironmentStackDetails
                                            uri={environmentUri}
                                            environmentLabel={environment.label}
                                        />
                                    ),
                                },
                                {
                                    label: 'sagemaker',
                                    component: (
                                        <SageMakerView
                                            environment={environment}
                                        />
                                    ),
                                },
                            ]}
                            onChange={(value) => setSelectedTab(value)}
                        />

                        {selectedTab === 0 && (
                            <div className="info-container">
                                <div className="flex justify-between items-center mb-16 text-true-blue ">
                                    <h3 className="text-2xl font-bold">
                                        Accounts
                                    </h3>
                                </div>

                                <NTable
                                    loadXMoreRowsAtATime={
                                        detailsLoading ? 5 : undefined
                                    }
                                    headers={tableHeaders}
                                    items={
                                        detailsLoading
                                            ? []
                                            : adjustItems(tableAccounts)
                                    }
                                    columnBorder={true}
                                    rowKey="name"
                                />
                            </div>
                        )}
                    </div>
                )
            )}
        </div>
    );
};

export default EnvironmentView;
